import React, { useEffect, useState } from "react";
import { Home, MapPin, ShoppingCart, User, PhoneCall, Search } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { collection, query, where, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import logo3 from "../assets/logo3.png";

const Header = () => {
  const { cartItems } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // get current route
  const [latestOrderId, setLatestOrderId] = useState(null);

  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Fetch latest order ID of current user
  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, "orders"),
      where("userId", "==", currentUser.uid),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const unsubscribe = onSnapshot(q, (snap) => {
      if (!snap.empty) {
        setLatestOrderId(snap.docs[0].id);
      } else {
        setLatestOrderId(null);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Show search box on all pages EXCEPT SearchPage
  const showHeaderSearch = location.pathname !== "/searchpage";

  return (
    <>
      {/* Top Header */}
      <header className="bg-white shadow-md top-0 z-50 sticky">
        <div className="max-w-7xl mx-auto px-1 py-1 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-2 text-2xl font-extrabold text-blue-600 tracking-tight">
            <img
              src={logo3}
              alt="logo3"
              className="h-12 w-12 object-cover rounded-full"
            />
            <Link to="/" className="font">
              Sajilo<span className="text-gray-800">Bazar</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex gap-8 text-gray-700 text-lg">
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <Link to="/stats" className="hover:text-blue-600">Stats</Link>
            <Link to={"/OrderTracking"} className={`hover:text-blue-600 cursor-not-allowed`}>
              Order Tracking
            </Link>
            <Link to="/contact" className="hover:text-blue-600">Contact</Link>
          </nav>

          {/* Desktop Right Side */}
          <div className="hidden md:flex items-center gap-6">
            {showHeaderSearch && (
              <button
                onClick={() => navigate("/searchpage")}
                className="p-2 rounded-full hover:bg-gray-100 transition "
              >
                <Search className="w-6 h-6 text-gray-700 hover:text-blue-600 " />
              </button>
            )}

            <Link to="/cart" className="relative">
              <ShoppingCart className="w-6 h-6 text-gray-700 hover:text-blue-600" />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {totalItems}
                </span>
              )}
            </Link>

            {!currentUser ? (
              <Link
                to="/signup"
                className="px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition font-semibold shadow-md"
              >
                Sign Up
              </Link>
            ) : (
              <Link to="/UserProfile">
                <User className="w-6 h-6 text-gray-700 hover:text-blue-600" />
              </Link>
            )}
          </div>

          {/* Mobile Right Icons */}
          <div className="md:hidden flex items-center gap-3">
            {showHeaderSearch && (
              <button onClick={() => navigate("/searchpage")}>
                <Search className="w-5 h-5 text-gray-700 mr-4" />
              </button>
            )}

            {!currentUser && (
              <Link
                to="/signup"
                className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-semibold text-sm"
              >
                Sign Up
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Sticky Search */}
        {showHeaderSearch && (
          <div className="md:hidden sticky top-0 z-40 bg-white px-4 py-2 shadow-md">
            <button
              onClick={() => navigate("/searchpage")}
              className="flex items-center gap-2 w-full px-4 py-3 border border-blue-500 rounded-lg text-gray-600"
            >
              <Search className="w-5 h-5 text-gray-500" />
              <span className="text-gray-500 text-sm">Search products...</span>
            </button>
          </div>
        )}
      </header>

      {/* Mobile Bottom Navigation */}
      <div className="fixed bottom-0 w-full bg-white shadow-[0_-4px_10px_rgba(0,0,0,0.15)] backdrop-blur-sm h-14 md:hidden flex justify-around items-center z-20 px-0">
        <Link
          to="/"
          className="flex items-center justify-center w-24 h-10 bg-yellow-400 rounded-xl hover:text-red-900 transition-colors duration-300 text-black shadow-md gap-2 px-3"
        >
          <Home className="w-5 h-5" />
          <span className="font-normal text-sm">Home</span>
        </Link>

        <Link
          to={"/OrderTracking"}
          className={`hover:text-blue-600 cursor-not-allowed`}
        >
          <MapPin className="w-5 h-5" />
        </Link>

        <Link
          to="/contact"
          className="flex items-center justify-center text-black hover:text-yellow-400 transition-colors duration-300 relative"
        >
          <PhoneCall className="w-5 h-5" />
        </Link>

        <Link
          to="/cart"
          className="flex items-center justify-center text-black hover:text-yellow-400 transition-colors duration-300 relative"
        >
          <ShoppingCart className="w-5 h-5" />
          {totalItems > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-black text-xs px-1 py-0.5 rounded-full">
              {totalItems}
            </span>
          )}
        </Link>

        <Link
          to="/UserProfile"
          className="flex items-center justify-center text-black hover:text-yellow-400 transition-colors duration-300"
        >
          <User className="w-5 h-5 text-gray-700" />
        </Link>
      </div>
    </>
  );
};

export default Header;
